/**
 * Minimal ISO-BMFF (MP4/MOV) top-level box scanner.
 *
 * Purpose: locate the `moov` atom (the playback index) so the chunked-upload
 * sender can upload the parts covering it right after the file header. For
 * standard (non-faststart) MP4s the moov sits at the END of the file, and a
 * receiver's <video> can't start playback until it has it — with sequential
 * uploads that means "after the whole upload". Reordering fixes that with a
 * few tiny header reads; no remuxing, no dependencies.
 *
 * The scanner only reads box HEADERS (8-16 bytes each via file.slice), never
 * box contents — cost is a handful of microreads regardless of file size.
 * Anything unexpected (non-BMFF file, malformed sizes) returns null and the
 * caller falls back to sequential upload order.
 */

export type ByteRange = {
  /** First byte of the box (inclusive) */
  start: number;
  /** One past the last byte of the box (exclusive) */
  end: number;
};

/** Plausible first top-level box types for ISO-BMFF files. */
const KNOWN_FIRST_BOX = new Set([
  "ftyp",
  "styp",
  "moov",
  "moof",
  "mdat",
  "free",
  "skip",
  "wide",
]);

/** Upper bound on top-level boxes scanned — real files have well under 20. */
const MAX_BOXES = 128;

const isPrintableType = (t: string) => /^[\x20-\x7e]{4}$/.test(t);

/**
 * Walks the file's top-level boxes and returns the byte range of the `moov`
 * atom, or null when the file isn't parseable ISO-BMFF / has no moov.
 */
export async function findMoovRange(file: Blob): Promise<ByteRange | null> {
  try {
    let offset = 0;
    let first = true;
    for (let i = 0; i < MAX_BOXES && offset < file.size; i++) {
      const header = new Uint8Array(
        await file
          .slice(offset, Math.min(offset + 16, file.size))
          .arrayBuffer(),
      );
      if (header.length < 8) {
        return null;
      }
      const view = new DataView(
        header.buffer,
        header.byteOffset,
        header.byteLength,
      );
      const size32 = view.getUint32(0);
      const type = String.fromCharCode(
        header[4],
        header[5],
        header[6],
        header[7],
      );
      if (!isPrintableType(type)) {
        return null;
      }
      if (first) {
        if (!KNOWN_FIRST_BOX.has(type)) {
          return null;
        }
        first = false;
      }

      let headerSize = 8;
      let boxSize: number;
      if (size32 === 1) {
        // 64-bit "largesize" in the 8 bytes after the type.
        if (header.length < 16) {
          return null;
        }
        const big = view.getBigUint64(8);
        if (big > BigInt(Number.MAX_SAFE_INTEGER)) {
          return null;
        }
        boxSize = Number(big);
        headerSize = 16;
      } else if (size32 === 0) {
        // Box extends to end of file (only legal for the last box).
        boxSize = file.size - offset;
      } else {
        boxSize = size32;
      }

      if (boxSize < headerSize || offset + boxSize > file.size) {
        return null; // malformed / truncated
      }
      if (type === "moov") {
        return { start: offset, end: offset + boxSize };
      }
      offset += boxSize;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Computes the 1-based part-number upload order for a chunked upload.
 *
 * Default is sequential. When the moov atom lives beyond the first two parts
 * (non-faststart file), the order becomes: part 1 (ftyp + file head — the
 * browser reads this before probing for moov), then the moov-bearing parts,
 * then everything else ascending. A receiver can then read the index within
 * seconds of the upload starting and play while the middle still uploads.
 */
export function computeChunkUploadOrder(
  fileSize: number,
  chunkSize: number,
  moovRange: ByteRange | null,
): number[] {
  const totalChunks = Math.max(1, Math.ceil(fileSize / chunkSize));
  const sequential = Array.from({ length: totalChunks }, (_, i) => i + 1);
  if (!moovRange || moovRange.end <= moovRange.start) {
    return sequential;
  }

  const firstMoovPart = Math.floor(moovRange.start / chunkSize) + 1;
  const lastMoovPart = Math.min(
    Math.floor((moovRange.end - 1) / chunkSize) + 1,
    totalChunks,
  );
  // moov already at/near the front (faststart) — sequential is optimal.
  if (firstMoovPart <= 2) {
    return sequential;
  }

  const priority = [1];
  for (let p = firstMoovPart; p <= lastMoovPart; p++) {
    priority.push(p);
  }
  const prioritized = new Set(priority);
  return [...priority, ...sequential.filter((p) => !prioritized.has(p))];
}
